import React, { useEffect, useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Box, Button, Callout } from '@node-space/ui-components'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import { useRewardsSheetContext } from 'pages/Rewards/context/RewardsSheetProvider'
import { useRewardsWithdraw } from 'pages/Rewards/hooks/api/useRewardsWithdraw'
import useRewardsTracking from 'pages/Rewards/hooks/useRewardsTracking'
import { RewardsWithdrawFormData } from '../../types'
import { RewardsAmountInput } from './RewardsAmountInput'
import { RewardsCurrencySelect } from './RewardsCurrencySelect'
import { RewardsWalletSelect } from './RewardsWalletSelect'
import { getWithdrawValidationSchema } from './validationSchema'

export type WithdrawMode = 'standard' | 'removeAllocation'

interface RewardsWithdrawFormProps {
  withdrawType: WithdrawMode
}

export const RewardsWithdraw: React.FC<RewardsWithdrawFormProps> = ({ withdrawType }) => {
  const { t } = useTranslation()
  const { requestError, setRequestError } = useMappedErrorMessage()
  const { trackFormInitiate, trackFormCancel } = useRewardsTracking()

  const { rewardsSheetAction, selectedCurrency: portfolioCurrency } = useRewardsSheetContext()

  const isRemoveAllocation = withdrawType === 'removeAllocation'

  const formMethods = useForm<RewardsWithdrawFormData>({
    resolver: yupResolver(getWithdrawValidationSchema(t)),
    mode: 'onChange',
    defaultValues: {
      currency: portfolioCurrency,
      withdrawToWallet: null,
      amount: '',
    },
  })
  const { handleSubmit, setValue, watch } = formMethods
  const selectedCurrency = watch('currency')

  const [maxAmount, setMaxAmount] = useState(0)

  const { onRewardsWithdraw, isRewardsWithdrawPending } = useRewardsWithdraw({
    isRemoveAllocation,
    onSuccess: () => {
      rewardsSheetAction?.onClose?.()
    },
    onError: error => {
      setRequestError({
        show: true,
        errorCode: error?.status,
        handledMessage: error?.message,
      })
    },
  })

  useEffect(() => {
    trackFormInitiate(isRemoveAllocation ? 'removeAllocation' : 'withdraw')
  }, [isRemoveAllocation])

  useEffect(() => {
    if (selectedCurrency) {
      setValue('withdrawToWallet', null)
      setValue('amount', '')
    }
  }, [selectedCurrency, setValue])

  const onCancel = () => {
    trackFormCancel(isRemoveAllocation ? 'removeAllocation' : 'withdraw')
    rewardsSheetAction?.onClose?.()
  }

  const onSubmit = (data: RewardsWithdrawFormData) => {
    setRequestError(null)
    onRewardsWithdraw(data)
  }

  const setMaxAmountFromPortfolio = (value: number) => {
    setMaxAmount(value)
  }

  return (
    <FormProvider {...formMethods}>
      <Box
        tag="form"
        flexCol
        gapY={16}
        width="full"
        paddingY={24}
        onSubmit={handleSubmit(onSubmit)}
      >
        {requestError?.show && (
          <Callout variant="error" testId="withdraw-error-callout">
            {requestError?.handledMessage}
          </Callout>
        )}

        <RewardsCurrencySelect
          selectedCurrency={selectedCurrency}
          disabled={!!portfolioCurrency}
          filterByBalance={isRemoveAllocation ? 'blocked' : undefined}
          setMaxAmount={setMaxAmountFromPortfolio}
        />

        <RewardsWalletSelect
          selectedCurrency={selectedCurrency}
          name="withdrawToWallet"
          disabled={!selectedCurrency}
          testId="withdraw-to-wallet-combobox"
        />

        <RewardsAmountInput
          selectedCurrency={selectedCurrency}
          maxAmount={maxAmount}
          disabled={!selectedCurrency}
          label={isRemoveAllocation ? t('rewards.removeAllocation.amountToRemove') : t('amount')}
        />

        <Box flex justify="between" gapX={12} paddingT={24}>
          <Button onClick={onCancel} variant="secondary" type="button" testId="cancel-button">
            {t('cancel')}
          </Button>
          <Button
            variant="primary"
            type="submit"
            testId="withdraw-button"
            disabled={isRewardsWithdrawPending}
            loading={isRewardsWithdrawPending}
          >
            {t('rewards.withdrawCurrency', {
              currency: selectedCurrency,
            })}
          </Button>
        </Box>
      </Box>
    </FormProvider>
  )
}
